/**
 * Multi-language translations for HEBE Guest Services
 * Supports: English (en), Indonesian (id)
 */

export const translations = {
  en: {
    // Header
    brandName: "HEBE",
    brandSubtitle: "Guest Services",
    menu: "Menu",

    header: {
      changeProfile: "Change Profile",
      backToOrders: "Back to my orders",
    },

    // Welcome Section
    welcomeTo: "Welcome to HEBE,",
    guestName: "Guest Name",
    welcomeDescription:
      "Bogor, West Java, Indonesia • Experience sustainable urban travel with personalized hospitality services",

    // Services
    services: {
      selfDeposit: {
        title: "My Bookings",
        description: "Check-in deposit & deposit refund",
      },
      myOrder: {
        title: "My Orders",
        description: "View and track your orders",
      },
      foodBeverages: {
        title: "Food & Beverages",
        description: "Order meals and drinks",
      },
      roomServices: {
        title: "Add-On Facility",
        description: "Extra bed, Nintendo rent, swimming ring rent",
      },
      activities: {
        title: "Activities",
        description: "Explore local experiences",
      },
      ecoTours: {
        title: "Eco Tours",
        description: "Sustainable adventures",
      },
      lifestyle: {
        title: "Lifestyle",
        description: "Shopping and wellness",
      },
      info: {
        title: "Info",
        description: "Hotel information",
      },
    },

    // Your Bookings hub
    yourBookings: {
      title: "My Bookings",
      subtitle:
        "Manage your stay's deposit — pay it online and track its refund.",
      intro:
        "Everything tied to your room deposit lives here. Use the options below to pay your check-in deposit online before you arrive, or learn how your deposit is returned after you check out.",
      selfDeposit: {
        title: "Self Check-in Deposit",
        what: "Pay your room deposit and the small online service fee online — no need to queue at the front desk.",
        why: "Once your deposit is settled you become eligible for self check-in. Your room is ready to use as soon as housekeeping clears it (normally by 14:00).",
        action: "Pay deposit now",
      },
      refund: {
        title: "Deposit Refund",
        what: "Your security deposit is returned after you check out, once housekeeping confirms there is no damage or missing item in the room.",
        why: "When it's ready, our staff send you a WhatsApp link to claim the refund to your account. The one-time online service fee is never refunded.",
        status: "Available after check-out",
      },
      list: {
        notSignedInTitle: "Find your booking",
        notSignedInBody:
          "Look up your booking and verify it's you to see your stays and pay your deposit online.",
        findBooking: "Find my booking",
        loadError: "We couldn't load your bookings. Please try again.",
        todayHeading: "Checking in today",
        upcomingHeading: "Upcoming stays",
        empty: "No active bookings found for your account.",
        nights: "nights",
        rooms: "rooms",
        badgePaid: "Deposit paid",
        badgeDue: "Deposit due",
        badgeExpired: "Link expired",
        continue: "Continue to deposit",
        viewInfo: "View check-in instructions",
        seeBookingDetail: "See booking detail",
        checkInInstructionHint:
          "Check-in instructions, room status and your deposit refund are all inside.",
        availableOnDay: "Deposit opens on your check-in day",
        refundHeading: "Past stays — deposit refund",
        refundHint:
          "Open your booking to view its status and claim your deposit refund.",
        badgeRefund: "Refund available",
        checkIn: "Check-in",
        checkOut: "Check-out",
      },
    },

    bookingDetail: {
      title: "Booking Detail",
      loadError: "We couldn't load this booking. Please try again.",
      claimError: "We couldn't start your refund. Please try again.",
      nights: "nights",
      rooms: "rooms",
      room: "Room",
      roomNumber: "Room",
      roomTbd: "Assigned after payment",
      checkIn: "Check-in",
      checkOut: "Check-out",
      lineItems: "Your rooms",
      depositSection: "Deposit",
      depositPending: "Your deposit is not paid yet.",
      goToPayment: "Pay deposit",
      checkInInstructionsTitle: "Self check-in",
      checkInInstructionsHint:
        "Your deposit is paid. Open your check-in instructions and booking QR code.",
      viewCheckInInstructions: "View check-in instructions",
      lineStatus: {
        reserved: "Not checked in",
        checked_in: "Staying",
        checked_out: "Checked out",
        canceled: "Canceled",
      },
      refundStateLabel: {
        not_eligible: "",
        awaiting_hk: "Housekeeping check pending",
        held: "On hold — hotel reviewing an issue",
        refundable: "Ready to refund",
        claimed: "Refund in progress",
        refunded: "Refunded",
        kept: "Not refundable (deducted)",
      },
      refundSection: "Deposit Refund",
      refundableNow: "Refundable now",
      feeNote: "Online service fee (non-refundable)",
      claimRefund: "Claim refund",
      pendingReview: "Awaiting hotel review",
      refundWaitingHk:
        "Your refund will be ready once all rooms are checked out and housekeeping has verified them.",
      refundNotAvailable:
        "Available after check-out and housekeeping verification.",
      refundBatches: "Refund payouts",
      batchStatus: {
        pending: "Preparing",
        payout_created: "Ready to claim",
        whatsapp_sent: "Ready to claim",
        token_verified: "Claimed",
        completed: "Completed",
        failed: "Failed",
        expired: "Expired",
        canceled: "Canceled",
      },
      openPayout: "Claim",
    },

    exitSession: {
      button: "Exit & start a new booking",
      confirmTitle: "Exit and start over?",
      confirmBody:
        "This signs you out of this booking and returns to the start, where you can look up a different booking. Your booking and any paid deposit are not affected.",
      confirmCancel: "Cancel",
      confirmYes: "Exit",
    },

    // Quick Access
    quickAccess: {
      title: "Need Immediate Assistance?",
      description: "Contact our 24/7 concierge service",
      button: "Call Now",
    },

    // Room Input
    room: {
      title: "Who's ordering?",
      modalHint:
        "Fill room number first. If you are not a hotel guest, fill your name.",
      tabGuest: "Hotel Guest",
      tabWalkin: "Walk-in",
      label: "Room Number",
      nameLabel: "Your Name",
      namePlaceholder: "Enter your name",
      nameOptionalHint: "Optional — we'll use your check-in name if blank",
      placeholder: "Enter your room number",
      nonGuestHint: "If you are not a hotel guest, please put your name here.",
      walkinHint: "Just tell us your name and we'll get your order started.",
      submit: "Continue",
      error: "Please fill room number or your name.",
      errorRoomRequired: "Please enter your room number.",
      errorNameRequired: "Please enter your name.",
      loading: "Saving...",
      guestInfo: "Guest Information",
      roomNumber: "Room",
    },

    // Food & Beverages Ordering
    order: {
      title: "Food & Beverages",
      backButton: "Back",
      categories: "Categories",
      available: "Available",
      outOfStock: "Out of Stock",
      addToCart: "Add to Cart",
      viewCart: "View Cart",
      quantity: "Quantity",
      price: "Price",
      total: "Total",
      freshlyPrepared: "Freshly prepared for you",
      pickYourMenu:
        "Pick your menu, adjust quantity, add notes, then continue to payment.",
      pickupInfo:
        "Orders are for self pick-up on the 1st floor, near the kitchen / lift.",
      searchPlaceholder: "Search menu...",
      searchTyping: "Searching…",
      searchResults: "results",
      noResults: "No menu items found",
      loadingOrders: "Loading orders...",
      noOrdersFound: "No orders found on this device yet.",
      loadingOrder: "Loading order...",
      itemNotePlaceholder: "Item note (max 200 chars)",
      cart: {
        title: "Shopping Cart",
        empty: "Your cart is empty",
        checkout: "Proceed to Checkout",
        subtotal: "Subtotal",
        tax: "Tax",
        total: "Total",
        remove: "Remove",
        each: "each",
      },
      payment: {
        title: "Review & Pay",
        method: "Payment Method",
        creditCard: "Credit Card",
        debitCard: "Debit Card",
        cash: "Cash",
        processing: "Processing Payment...",
        confirm: "Confirm Payment",
        cancel: "Cancel Order",
        orderSummary: "Order Summary",
        orderDetails: "Order Details",
        yourName: "Your Name",
        enterYourName: "Enter your name",
        emailOptional: "Email (optional, for receipt)",
        emailOptionalPlain: "Email (optional)",
        emailReceipt: "Email me the receipt",
        emailReceiptRequired: "Enter your email to receive the receipt.",
      },
      pickupNotice: {
        title: "This order is for self pick-up",
        where:
          "Collect it yourself on the 1st floor, near the kitchen / lift. There is no delivery to your room.",
        notifyOn: "We will notify you here the moment the kitchen finishes it.",
        notifyOff:
          "Notifications are off, so open this order again and refresh to see when it is ready.",
        redirect: "Taking you to the payment page in %{seconds}...",
      },
      voucher: {
        title: "Voucher / Coupon",
        placeholder: "Enter voucher code",
        apply: "Apply",
        checking: "Checking...",
        discount: "Voucher discount",
        applied: "Voucher applied",
        autoApplied: "🎟️ Voucher applied automatically from your link!",
        remove: "Remove",
        invalid: "Voucher code is not valid.",
      },
      confirmation: {
        title: "Order Confirmed!",
        orderNumber: "Order Number",
        estimatedTime: "Estimated Pickup Time",
        trackOrder: "Track Order",
        returnMenu: "Return to Menu",
        status: "Order Status",
        preparing: "Preparing your order...",
        ready: "Ready for Pickup",
        pickedUp: "Picked Up",
      },
    },

    orders: {
      title: "My Orders",
      orderMore: "Order More",
      orderMoreFnb: "Order food",
      orderMoreAddOn: "Order facilities",
    },

    kitchen: {
      open: "Kitchen Open",
      closed: "Kitchen Closed",
      closedToday: "Closed Today",
      opensAt: "Opens at",
      closesAt: "Closes at",
      viewSchedule: "View Schedule",
      viewHours: "View Hours",
      operatingHours: "Kitchen Operating Hours",
      closedMessage:
        "The kitchen is currently closed. You can browse the menu, but ordering is not available right now.",
      closedCheckout: "Kitchen is currently closed",
    },

    orderDetail: {
      invoiceNumber: "Invoice No. (show to staff to verify)",
      paymentStatus: "Payment status",
      deliveryStatus: "Delivery status",
      totalAmount: "Total amount",
      subtotal: "Subtotal",
      discount: "Discount",
      orderedAt: "Ordered at",
      paidAt: "Paid at",
      finishedAt: "Finished at",
      openPaymentLink: "Open Payment Link",
      orderItems: "Order Items",
      notifications: "Notifications",
      noNotificationsYet: "No notifications yet.",
      notePrefix: "Note",
      paid: "Paid",
      pending: "Pending",
      onProgress: "On Progress",
      finished: "Finished",
      pendingPayment: "Pending Payment",
      pickupTitle: "Pick up your order yourself",
      pickupWhereReady: "We will let you know the moment the kitchen finishes.",
      pickupWhereRefresh: "Refresh this page to check whether it is ready.",
      // Keyed on OrderNotification#notification_type, not on the stored text: the server writes those
      // rows in English and they are already in the database, so translating at the source would
      // leave every existing row untranslated and need a backfill. Unknown types fall back to the
      // stored title/message, which is what makes this safe for types added later.
      notificationCopy: {
        order_finished: {
          title: "Order ready for pick-up",
          message:
            "Order %{number} is finished and ready to be picked up on the 1st floor, near the kitchen / lift.",
        },
      },
    },

    // Footer (HGS)
    footer: {
      copyright: "© 2025 HEBE - Hotel, Hostel & Hospitality Network",
      tagline: "Sustainable Urban Travel Experience",
    },

    notifications: {
      on: "On",
      off: "Off",
      blocked: "Blocked",
      alreadyEnabled:
        "Notifications are enabled. You'll receive updates when your order is ready.",
      deniedMessage:
        "Notifications are blocked by your browser. To receive order updates, please enable notifications in your browser settings.",
      promptModal: {
        title: "Stay Updated on Your Order",
        description:
          "Enable notifications so we can let you know when your food is ready for pickup. You won't miss a thing!",
        enable: "Enable Notifications",
        enabling: "Enabling...",
        later: "Maybe Later",
      },
      deniedModal: {
        title: "Notifications Are Blocked",
        description:
          "It looks like notifications are blocked on your browser. Follow these steps to enable them:",
        gotIt: "Got It",
        steps: {
          ios: [
            "Open your iPhone Settings app",
            "Scroll down and tap Safari (or your browser)",
            "Tap Notifications and enable Allow Notifications",
            "Come back to this page and refresh",
          ],
          android: [
            "Tap the lock icon (or ⋮ menu) in your browser's address bar",
            "Tap Site settings or Permissions",
            "Find Notifications and change it to Allow",
            "Come back to this page and refresh",
          ],
          androidFirefox: [
            "Tap the lock icon in the address bar",
            "Tap Edit Site Settings",
            "Set Notification to Allowed",
            "Come back to this page and refresh",
          ],
          desktopChrome: [
            "Click the lock icon (or tune icon) in the address bar",
            "Find Notifications in the permissions list",
            "Change it from Block to Allow",
            "Refresh this page",
          ],
          desktopFirefox: [
            "Click the lock icon in the address bar",
            "Click on the Permissions section",
            "Find Notifications and remove the block",
            "Refresh this page",
          ],
          desktopSafari: [
            "Go to Safari menu → Settings → Websites",
            "Click Notifications in the sidebar",
            "Find this website and change it to Allow",
            "Refresh this page",
          ],
        },
      },
    },

    // Landing Page
    landing: {
      stories: {
        eyebrow: "Instagram & TikTok",
        heading: "From the property",
        body: "What the place actually looks like this week, posted by the people who work here.",
        follow: "Follow us",
        watchOn: "Watch on TikTok",
        viewOn: "View on Instagram",
        empty: "Nothing here yet — follow us in the meantime.",
      },
      nav: {
        book: "Book",
        stories: "Stories",
        guestPortal: "Staying with us",
        sections: "Explore",
        home: "Home",
        about: "About",
        rooms: "Rooms",
        facilities: "Facilities",
        gallery: "Gallery",
        insights: "Insights",
        contact: "Contact",
        bookNow: "Book Now",
      },
      hero: {
        eyebrow: "Eco hostel · Bogor, West Java",
        name: "Hebe Hostel Bogor",
        bookStay: "Book your stay",
        discover: "Discover more",
        scrollHint: "Scroll to content",
        slideLabel: "Show image {n} of {total}",
        galleryLabel: "Hero images",
        videoLabel: "A short silent tour of the property",
      },
      about: {
        subtitle: "About Us",
        title: "Welcome To",
        contentHeading: "Sustainable Urban Travel Experience",
        p1: "Our mission is to offer a sustainable urban travel experience that connects eco-conscious guests with local culture. We believe in maximizing rooftop capacity in the heart of the city, offering luxury camping with stunning urban views.",
        p2: "From our smart self-check-in system to our eco-friendly amenities, every aspect of Hebe Hotel is designed with sustainability and modern convenience in mind.",
        description:
          "A modern eco hostel in the heart of the city where nature meets technology. Guests enjoy smart self check-in, sustainable living, and a cozy urban vibe built for digital nomads and conscious travelers.",
        features: {
          eco: {
            title: "Eco-Friendly",
            desc: "Sustainable practices and green initiatives throughout our property",
          },
          location: {
            title: "Prime Location",
            desc: "In the heart of Bogor with easy access to city attractions",
          },
          service: {
            title: "Quality Service",
            desc: "50+ experienced staff dedicated to your comfort",
          },
          community: {
            title: "Community",
            desc: "Perfect for digital nomads and conscious travelers",
          },
        },
      },
      facilities: {
        subtitle: "Amenities",
        title: "Hotel Facilities",
        description:
          "Experience world-class amenities designed for your comfort and convenience",
        items: [
          {
            icon: "bed",
            title: "Rooms and Suites",
            description:
              "Varied types of rooms, from standard to luxury suites, equipped with modern essentials.",
          },
          {
            icon: "shield",
            title: "24-Hour Security",
            description:
              "On-site security personnel and best surveillance with secure storage for valuables.",
          },
          {
            icon: "pool",
            title: "Swimming Pool",
            description:
              "Indoor and outdoor pools for leisure or exercise with stunning city views.",
          },
          {
            icon: "wifi",
            title: "High-Speed WiFi",
            description:
              "Complimentary high-speed internet access throughout the property.",
          },
          {
            icon: "dining",
            title: "Restaurant & Bar",
            description:
              "On-site dining with local and international cuisine, open all day.",
          },
        ],
      },
      rooms: {
        fromLabel: "From",
        perNight: "per night",
        roomsUnit: "rooms",
        wifi: "Free WiFi",
        viewRoom: "View room",
        subtitle: "Accommodations",
        title: "Our Rooms",
        description:
          "Choose from our thoughtfully designed rooms, each crafted for comfort and sustainability",
        checkRates: "Check Rates & Availability",
      },
      gallery: {
        photos: "photos",
        viewImage: "View image",
        lightboxLabel: "Property photos",
        close: "Close",
        prev: "Previous image",
        next: "Next image",
        subtitle: "Gallery",
        title: "Our Property",
        description: "A glimpse into our spaces and experiences",
      },
      booking: {
        occupancyNote:
          "Every room sleeps two. A third or fourth guest is welcome with an extra bed, charged per room per night. Children under 5 and under 100 cm stay free and need no bed.",
        photos: "%{n} photos",
        viewPhotos: "View photos",
        poolAccess: "Pool access",
        selfCheckIn: "Self check-in",
        checkInFrom: "Check-in from %{t}",
        checkOutBy: "Check-out by %{t}",
        earlyCheckIn: "Early check-in",
        earlyCheckInNote:
          "Available on request, subject to availability on the day, for an extra charge.",
        stayPolicy: "Stay times",
        sleeps: "Sleeps up to %{n}",
        roomSize: "%{n} m²",
        freeWifi: "Free WiFi",
        perRoomPerNight: "per room, per night",
        includedTitle: "What every room includes",
        incl1: "Two guests, no extra charge",
        incl2: "Self check-in, any hour",
        incl3: "Free WiFi throughout",
        incl4: "Children under 5 stay free",
        selectDates: "Select dates",
        clearDates: "Clear",
        prevMonth: "Previous month",
        nextMonth: "Next month",
        addDates: "Add dates",
        maxStay: "Maximum stay is %{n} nights",
        doneDates: "Done",
        roomsLabel: "Rooms",
        eyebrow: "Check availability",
        heading: "Find your room",
        checkIn: "Check in",
        checkOut: "Check out",
        nights: "nights",
        night: "night",
        search: "Check availability",
        searching: "Checking…",
        perNight: "per night",
        totalFor: "Total for",
        available: "Available",
        limited: "Only a few left",
        soldOut: "Sold out",
        guests: "Guests",
        guestsHint: "Age 5+ or over 100cm",
        children: "Young children",
        childrenHint: "Under 5, under 100cm · free",
        extraBed: "Extra bed",
        extraBedNote:
          "Book direct with the front desk. There is no commission in the middle, so we can often do better than the listing sites — and you are talking to the people who will hand you your key.",
        add: "Add",
        remove: "Remove",
        added: "Added",
        yourStay: "Your stay",
        emptyBasket: "Pick a room to start.",
        roomsLine: "rooms",
        estimatedTotal: "Estimated total",
        bookViaWhatsapp: "Book via WhatsApp",
        priceNote:
          "Prices shown are indicative walk-in rates for the dates selected. Rates may change according to the property's own rules, and the hotel may revise them at any time at its sole discretion. Your final rate is confirmed by the front desk.",
        noRooms:
          "No rooms available for those dates. Try different dates or message us — we may still be able to help.",
        pickDates: "Pick your dates to see live rates and availability.",
        alsoOn: "Also available on",
        subtitle: "Book Your Stay",
        title: "Reserve Your Room",
        description:
          "Check live rates and availability with our booking partners",
        stats: {
          roomsLabel: "Rooms",
          roomTypesLabel: "Room types",
          supportLabel: "Front desk",
          supportValue: "24/7",
        },
      },
      footer: {
        tagline: "Eco-Tourism City Experience",
        description:
          "A modern eco hostel in the heart of the city where nature meets technology.",
        quickLinks: "Quick Links",
        services: "Services",
        contactUs: "Contact Us",
        newsletter: "Newsletter",
        newsletterDesc: "Subscribe for updates and exclusive offers",
        subscribe: "Subscribe",
        emailPlaceholder: "Enter your email",
        copyright: "Hebe Hostel Bogor. All rights reserved.",
        links: {
          home: "Home",
          about: "About Us",
          rooms: "Rooms",
          facilities: "Facilities",
          gallery: "Gallery",
          contact: "Contact",
        },
        serviceLinks: {
          fnb: "Food & Beverages",
          roomService: "Room Service",
          activities: "Activities",
          ecoTours: "Eco Tours",
        },
        privacyPolicy: "Privacy Policy",
        termsOfService: "Terms of Service",
      },
      guestWidget: {
        askTitle: "Any question? Message us on WhatsApp",
        askDesc: "Opens a chat with the front desk",
        title: "Guest Services",
        subtitle: "How can we help you?",
        guestServices: "Guest Services",
        guestServicesDesc: "Access all hotel services",
        orderFood: "Order Food & Beverages",
        orderFoodDesc: "Browse and order from our menu",
      },
      privacyPolicy: {
        title: "Privacy Policy",
        lastUpdated: "Last updated: March 2026",
        sections: [
          {
            heading: "1. Introduction",
            body: 'Hebe Hotel, Hostel & Hospitality Network ("we", "our", or "us"), operated by HostelNMC and located in Bogor, West Java, Indonesia, is committed to protecting your personal data. This Privacy Policy explains what information we collect, how we use it, and your rights under Indonesian law (UU PDP No. 27 Tahun 2022) and applicable international standards.',
          },
          {
            heading: "2. Information We Collect",
            body: "We collect information you provide when making a reservation or using our services, including: full name, email address, phone number, government-issued identification (KTP/passport), payment details, and stay preferences. We also automatically collect device information, IP address, browser type, and usage data when you visit our website.",
          },
          {
            heading: "3. How We Use Your Information",
            body: "Your information is used to: process and manage your booking, communicate reservation confirmations and updates, provide in-stay guest services, process payments securely, comply with Indonesian legal and tax obligations, improve our services and website, and send relevant offers or newsletters (only with your consent).",
          },
          {
            heading: "4. Data Sharing",
            body: "We may share your data with: online travel agencies (OTAs) through which your booking was made, payment processors (e.g. Xendit) for secure transaction handling, government authorities when required by Indonesian law, and service providers operating on our behalf under confidentiality agreements. We do not sell your personal data to third parties.",
          },
          {
            heading: "5. Data Security",
            body: "We implement industry-standard technical and organisational measures to protect your personal data against unauthorised access, alteration, disclosure, or destruction. Payment data is handled in accordance with PCI-DSS standards.",
          },
          {
            heading: "6. Your Rights",
            body: "Under UU PDP No. 27 Tahun 2022, you have the right to access, correct, or request deletion of your personal data. You may also withdraw consent for marketing communications at any time. To exercise these rights, contact us at admin@hostelnmc.com.",
          },
          {
            heading: "7. Cookies & Tracking",
            body: "Our website uses cookies and similar technologies to ensure functionality and analyse usage. Essential cookies are required for the site to operate; analytics cookies are only set with your consent. You can manage cookie preferences through your browser settings.",
          },
          {
            heading: "8. Contact",
            body: "For any questions about this Privacy Policy or your personal data, please contact our Data Protection Officer at admin@hostelnmc.com or write to us at Hebe Hotel, Bogor, West Java, Indonesia.",
          },
          {
            heading: "9. Deposit Communication & Automatic Refund",
            body: "If you use our self check-in deposit portal, we use the email address and WhatsApp number you confirm there to communicate about your deposit and to process its automatic refund after checkout: your WhatsApp receives the deposit-refund link and your email receives the payout details and confirmation. These contacts are processed by our payment and messaging partners (e.g. Xendit and the WhatsApp Business platform), which may involve transfer outside Indonesia under appropriate safeguards. We retain them for the duration of your stay and the refund-processing period. You are responsible for the accuracy of the details you enter; we are not liable for a refund that fails to reach you because of incorrect contact data you provided. You may correct your data or withdraw consent (subject to completing any in-progress refund) under UU PDP No. 27 Tahun 2022 by contacting admin@hostelnmc.com.",
          },
          {
            heading: "10. Policy Updates",
            body: "We may update this Privacy Policy from time to time. The date at the top of this page indicates when it was last revised. We encourage you to review this page periodically.",
          },
        ],
      },
      termsOfService: {
        title: "Terms of Service",
        lastUpdated: "Last updated: March 2026",
        sections: [
          {
            heading: "1. Agreement",
            body: 'By making a reservation or staying at Hebe Hotel, Hostel & Hospitality Network ("Hebe"), you agree to be bound by these Terms of Service. If you do not agree, please do not proceed with your booking.',
          },
          {
            heading: "2. Booking & Reservations",
            body: "Check-in time is 14:00 (2:00 PM) and check-out time is 12:00 (12:00 PM). Early check-in and late check-out are subject to availability and may incur additional charges. Cancellation policies vary by rate plan and will be clearly stated at the time of booking. No-shows may be charged the full reservation amount.",
          },
          {
            heading: "3. Guest Responsibilities",
            body: "Guests are responsible for any damage caused to the property, furniture, or equipment during their stay. Hebe reserves the right to charge for damages assessed after departure. Guests must ensure their behaviour does not disturb other guests or staff.",
          },
          {
            heading: "4. Payments",
            body: "Payment is required to confirm your reservation. We accept major credit/debit cards and selected digital payment methods. All prices are quoted in Indonesian Rupiah (IDR) inclusive of applicable taxes. Hebe reserves the right to modify pricing without prior notice, except for confirmed bookings.",
          },
          {
            heading: "5. Check-In Requirements",
            body: "All guests must present a valid government-issued photo ID (Indonesian KTP or international passport) at check-in. Guests under 18 must be accompanied by a parent or legal guardian. Hebe reserves the right to refuse check-in if valid identification cannot be provided.",
          },
          {
            heading: "6. Property Rules",
            body: "Smoking is strictly prohibited inside guest rooms. Designated smoking areas may be available in certain indoor and outdoor areas of our property — please ask a member of staff on duty if you are unsure where smoking is permitted. Quiet hours are observed between 22:00 and 07:00. Pets are not permitted on the premises. Any illegal activity will result in immediate removal from the property and may be reported to the authorities.",
          },
          {
            heading: "7. Limitation of Liability",
            body: "Hebe is not liable for loss, theft, or damage to personal belongings during your stay unless caused by our direct negligence. We recommend keeping valuables secured and in your possession at all times. Our liability, if any, is limited to the amount paid for the accommodation.",
          },
          {
            heading: "8. Governing Law",
            body: "These Terms of Service are governed by the laws of the Republic of Indonesia. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts in Bogor, West Java, Indonesia.",
          },
          {
            heading: "9. Guest Contact Details & Automatic Deposit Refund",
            body: "When you pay your deposit online and confirm your email and WhatsApp number, you warrant that those details are correct and current, and you consent to Hebe using them to communicate about your stay and to deliver your automatic deposit refund (the refund link is sent via WhatsApp and the payout details via email). You are solely responsible for the accuracy of the contact details you provide; Hebe is not liable for a delayed or failed deposit refund caused by incorrect, inactive, or mistyped contact information you entered. The online service fee is non-refundable.",
          },
          {
            heading: "10. Contact",
            body: "If you have any questions regarding these Terms of Service, please contact us at admin@hostelnmc.com or visit us at Hebe Hotel, Bogor, West Java, Indonesia.",
          },
        ],
      },
      insights: {
        brand: "Insights",
        tagline: "Stories, guides and local know-how from Bogor",
        index: {
          title: "HEBE Insights",
          subtitle:
            "Stories, guides and local know-how for a richer, greener stay in Bogor.",
          featured: "Featured",
          viewAll: "View all",
          exploreCategory: "Explore",
          latestIn: "Latest in",
        },
        card: {
          minRead: "min read",
          by: "By",
        },
        detail: {
          backToInsights: "Back to Insights",
          by: "By",
          minRead: "min read",
          inCategory: "in",
          share: "Share this story",
          related: "Related stories",
          copyLink: "Copy link",
          copied: "Copied!",
        },
        category: {
          articles: "articles",
          allArticles: "All articles",
          empty: "No articles here yet. Check back soon.",
        },
        pagination: {
          prev: "Previous",
          next: "Next",
          page: "Page",
          of: "of",
        },
        breadcrumb: {
          home: "Home",
          insights: "Insights",
        },
        share: {
          whatsapp: "WhatsApp",
          facebook: "Facebook",
          x: "X",
          telegram: "Telegram",
          line: "LINE",
          linkedin: "LinkedIn",
          pinterest: "Pinterest",
          email: "Email",
          copy: "Copy link",
        },
      },
    },
  },

  id: {
    // Header
    brandName: "HEBE",
    brandSubtitle: "Layanan Tamu",
    menu: "Menu",

    header: {
      changeProfile: "Ubah Profil",
      backToOrders: "Kembali ke pesanan saya",
    },

    // Welcome Section
    welcomeTo: "Selamat datang di HEBE,",
    guestName: "Nama Tamu",
    welcomeDescription:
      "Bogor, Jawa Barat, Indonesia • Nikmati pengalaman perjalanan urban berkelanjutan dengan layanan perhotelan yang personal",

    // Services
    services: {
      selfDeposit: {
        title: "Bookingan Saya",
        description: "Deposit check-in & pengembalian deposit",
      },
      myOrder: {
        title: "Pesanan Saya",
        description: "Lihat dan lacak pesanan Anda",
      },
      foodBeverages: {
        title: "Makanan & Minuman",
        description: "Pesan makanan dan minuman",
      },
      roomServices: {
        title: "Fasilitas Tambahan",
        description: "Kasur tambahan, sewa Nintendo, sewa ban renang",
      },
      activities: {
        title: "Aktivitas",
        description: "Jelajahi pengalaman lokal",
      },
      ecoTours: {
        title: "Wisata Eko",
        description: "Petualangan berkelanjutan",
      },
      lifestyle: {
        title: "Gaya Hidup",
        description: "Belanja dan kesehatan",
      },
      info: {
        title: "Informasi",
        description: "Informasi hotel",
      },
    },

    // Your Bookings hub
    yourBookings: {
      title: "Bookingan Saya",
      subtitle:
        "Kelola deposit menginap Anda — bayar online dan pantau pengembaliannya.",
      intro:
        "Semua yang terkait deposit kamar Anda ada di sini. Gunakan pilihan di bawah untuk membayar deposit check-in secara online sebelum tiba, atau pelajari bagaimana deposit Anda dikembalikan setelah check-out.",
      selfDeposit: {
        title: "Deposit Check-in Mandiri",
        what: "Bayar deposit kamar dan biaya layanan online yang kecil secara online — tanpa antre di resepsionis.",
        why: "Setelah deposit lunas, Anda memenuhi syarat untuk check-in mandiri. Kamar siap digunakan begitu housekeeping menyatakan bersih (umumnya pukul 14.00).",
        action: "Bayar deposit sekarang",
      },
      refund: {
        title: "Pengembalian Deposit",
        what: "Deposit keamanan Anda dikembalikan setelah check-out, setelah housekeeping memastikan tidak ada kerusakan atau barang hilang di kamar.",
        why: "Saat siap, staf kami mengirim tautan WhatsApp untuk mencairkan pengembalian ke rekening Anda. Biaya layanan online sekali bayar tidak dapat dikembalikan.",
        status: "Tersedia setelah check-out",
      },
      list: {
        notSignedInTitle: "Temukan pemesanan Anda",
        notSignedInBody:
          "Cari pemesanan Anda dan verifikasi identitas untuk melihat masa inap dan membayar deposit secara online.",
        findBooking: "Cari pemesanan saya",
        loadError: "Tidak dapat memuat pemesanan Anda. Silakan coba lagi.",
        todayHeading: "Check-in hari ini",
        upcomingHeading: "Masa inap mendatang",
        empty: "Tidak ada pemesanan aktif untuk akun Anda.",
        nights: "malam",
        rooms: "kamar",
        badgePaid: "Deposit lunas",
        badgeDue: "Deposit belum dibayar",
        badgeExpired: "Tautan kedaluwarsa",
        continue: "Lanjut ke deposit",
        viewInfo: "Lihat instruksi check-in",
        seeBookingDetail: "Lihat detail pemesanan",
        checkInInstructionHint:
          "Instruksi check-in, status kamar, dan refund deposit Anda ada di dalam.",
        availableOnDay: "Deposit dibuka pada hari check-in Anda",
        refundHeading: "Masa inap selesai — refund deposit",
        refundHint:
          "Buka pemesanan untuk melihat status dan mencairkan refund deposit Anda.",
        badgeRefund: "Refund tersedia",
        checkIn: "Check-in",
        checkOut: "Check-out",
      },
    },

    bookingDetail: {
      title: "Detail Pemesanan",
      loadError: "Tidak dapat memuat pemesanan ini. Silakan coba lagi.",
      claimError: "Tidak dapat memulai refund Anda. Silakan coba lagi.",
      nights: "malam",
      rooms: "kamar",
      room: "Kamar",
      roomNumber: "Kamar",
      roomTbd: "Ditentukan setelah pembayaran",
      checkIn: "Check-in",
      checkOut: "Check-out",
      lineItems: "Kamar Anda",
      depositSection: "Deposit",
      depositPending: "Deposit Anda belum dibayar.",
      goToPayment: "Bayar deposit",
      checkInInstructionsTitle: "Check-in mandiri",
      checkInInstructionsHint:
        "Deposit Anda sudah dibayar. Buka instruksi check-in dan kode QR pemesanan Anda.",
      viewCheckInInstructions: "Lihat instruksi check-in",
      lineStatus: {
        reserved: "Belum check-in",
        checked_in: "Sedang menginap",
        checked_out: "Sudah check-out",
        canceled: "Dibatalkan",
      },
      refundStateLabel: {
        not_eligible: "",
        awaiting_hk: "Menunggu pemeriksaan housekeeping",
        held: "Ditahan — hotel sedang meninjau masalah",
        refundable: "Siap direfund",
        claimed: "Refund sedang diproses",
        refunded: "Telah direfund",
        kept: "Tidak dapat direfund (dipotong)",
      },
      refundSection: "Refund Deposit",
      refundableNow: "Dapat direfund sekarang",
      feeNote: "Biaya layanan online (tidak dikembalikan)",
      claimRefund: "Klaim refund",
      pendingReview: "Menunggu peninjauan hotel",
      refundWaitingHk:
        "Refund Anda siap setelah semua kamar check-out dan housekeeping memverifikasinya.",
      refundNotAvailable:
        "Tersedia setelah check-out dan verifikasi housekeeping.",
      refundBatches: "Pembayaran refund",
      batchStatus: {
        pending: "Menyiapkan",
        payout_created: "Siap diklaim",
        whatsapp_sent: "Siap diklaim",
        token_verified: "Telah diklaim",
        completed: "Selesai",
        failed: "Gagal",
        expired: "Kedaluwarsa",
        canceled: "Dibatalkan",
      },
      openPayout: "Klaim",
    },

    exitSession: {
      button: "Keluar & mulai pemesanan baru",
      confirmTitle: "Keluar dan mulai dari awal?",
      confirmBody:
        "Ini akan mengakhiri sesi pemesanan ini dan kembali ke awal, di mana Anda dapat mencari pemesanan lain. Pemesanan dan deposit yang sudah dibayar tidak terpengaruh.",
      confirmCancel: "Batal",
      confirmYes: "Keluar",
    },

    // Quick Access
    quickAccess: {
      title: "Butuh Bantuan Segera?",
      description: "Hubungi layanan concierge 24/7 kami",
      button: "Hubungi Sekarang",
    },

    // Room Input
    room: {
      title: "Siapa yang memesan?",
      modalHint:
        "Isi nomor kamar terlebih dahulu. Jika bukan tamu hotel, isi nama Anda.",
      tabGuest: "Tamu Hotel",
      tabWalkin: "Walk-in",
      label: "Nomor Kamar",
      nameLabel: "Nama Anda",
      namePlaceholder: "Masukkan nama Anda",
      nameOptionalHint:
        "Opsional — kami akan gunakan nama check-in jika kosong",
      placeholder: "Masukkan nomor kamar Anda",
      nonGuestHint:
        "Jika Anda bukan tamu hotel, silakan isi nama Anda di sini.",
      walkinHint: "Cukup isi nama Anda, dan pesanan akan segera diproses.",
      submit: "Lanjutkan",
      error: "Silakan isi nomor kamar atau nama Anda.",
      errorRoomRequired: "Silakan masukkan nomor kamar.",
      errorNameRequired: "Silakan masukkan nama Anda.",
      loading: "Menyimpan...",
      guestInfo: "Informasi Tamu",
      roomNumber: "Kamar",
    },

    // Food & Beverages Ordering
    order: {
      title: "Makanan & Minuman",
      backButton: "Kembali",
      categories: "Kategori",
      available: "Tersedia",
      outOfStock: "Habis",
      addToCart: "Tambah ke Keranjang",
      viewCart: "Lihat Keranjang",
      quantity: "Jumlah",
      price: "Harga",
      total: "Total",
      freshlyPrepared: "Disiapkan segar untuk Anda",
      pickYourMenu:
        "Pilih menu, atur jumlah, tambah catatan, lalu lanjut ke pembayaran.",
      pickupInfo: "Pesanan diambil sendiri di lantai 1, dekat dapur / lift.",
      searchPlaceholder: "Cari menu...",
      searchTyping: "Mencari…",
      searchResults: "hasil",
      noResults: "Tidak ada menu yang ditemukan",
      loadingOrders: "Memuat pesanan...",
      noOrdersFound: "Belum ada pesanan di perangkat ini.",
      loadingOrder: "Memuat pesanan...",
      itemNotePlaceholder: "Catatan item (maks 200 karakter)",
      cart: {
        title: "Keranjang Belanja",
        empty: "Keranjang Anda kosong",
        checkout: "Lanjutkan ke Pembayaran",
        subtotal: "Subtotal",
        tax: "Pajak",
        total: "Total",
        remove: "Hapus",
        each: "per item",
      },
      payment: {
        title: "Tinjau & Bayar",
        method: "Metode Pembayaran",
        creditCard: "Kartu Kredit",
        debitCard: "Kartu Debit",
        cash: "Tunai",
        processing: "Memproses Pembayaran...",
        confirm: "Konfirmasi Pembayaran",
        cancel: "Batal Pesanan",
        orderSummary: "Ringkasan Pesanan",
        orderDetails: "Detail Pesanan",
        yourName: "Nama Anda",
        enterYourName: "Masukkan nama Anda",
        emailOptional: "Email (opsional, untuk struk)",
        emailOptionalPlain: "Email (opsional)",
        emailReceipt: "Kirim struk ke email saya",
        emailReceiptRequired: "Isi email untuk menerima struk.",
      },
      pickupNotice: {
        title: "Pesanan ini diambil sendiri",
        where:
          "Ambil sendiri di lantai 1, dekat dapur / lift. Tidak ada pengantaran ke kamar.",
        notifyOn: "Kami beri tahu di sini begitu dapur selesai memasak.",
        notifyOff:
          "Notifikasi belum aktif, jadi buka lagi pesanan ini dan muat ulang untuk melihat kapan siap.",
        redirect: "Mengalihkan ke halaman pembayaran dalam %{seconds}...",
      },
      voucher: {
        title: "Voucher / Kupon",
        placeholder: "Masukkan kode voucher",
        apply: "Pakai",
        checking: "Memeriksa...",
        discount: "Diskon voucher",
        applied: "Voucher dipakai",
        autoApplied: "🎟️ Voucher otomatis diterapkan dari link Anda!",
        remove: "Hapus",
        invalid: "Kode voucher tidak valid.",
      },
      confirmation: {
        title: "Pesanan Dikonfirmasi!",
        orderNumber: "Nomor Pesanan",
        estimatedTime: "Waktu Pengambilan Estimasi",
        trackOrder: "Lacak Pesanan",
        returnMenu: "Kembali ke Menu",
        status: "Status Pesanan",
        preparing: "Menyiapkan pesanan Anda...",
        ready: "Siap Diambil",
        pickedUp: "Sudah Diambil",
      },
    },

    orders: {
      title: "Pesanan Saya",
      orderMore: "Pesan Lagi",
      orderMoreFnb: "Pesan makanan",
      orderMoreAddOn: "Pesan fasilitas",
    },

    kitchen: {
      open: "Dapur Buka",
      closed: "Dapur Tutup",
      closedToday: "Tutup Hari Ini",
      opensAt: "Buka pukul",
      closesAt: "Tutup pukul",
      viewSchedule: "Lihat Jadwal",
      viewHours: "Lihat Jam",
      operatingHours: "Jam Operasional Dapur",
      closedMessage:
        "Dapur sedang tutup. Anda dapat melihat menu, tetapi pemesanan belum tersedia saat ini.",
      closedCheckout: "Dapur sedang tutup",
    },

    orderDetail: {
      invoiceNumber: "No. Invoice (tunjukkan ke staf untuk verifikasi)",
      paymentStatus: "Status pembayaran",
      deliveryStatus: "Status pengiriman",
      totalAmount: "Jumlah total",
      subtotal: "Subtotal",
      discount: "Diskon",
      orderedAt: "Dipesan pada",
      paidAt: "Dibayar pada",
      finishedAt: "Selesai pada",
      openPaymentLink: "Buka Link Pembayaran",
      orderItems: "Item Pesanan",
      notifications: "Notifikasi",
      noNotificationsYet: "Belum ada notifikasi.",
      notePrefix: "Catatan",
      paid: "Lunas",
      pending: "Menunggu",
      onProgress: "Dalam Proses",
      finished: "Selesai",
      pendingPayment: "Menunggu Pembayaran",
      pickupTitle: "Pesanan diambil sendiri",
      pickupWhereReady: "Kami beri tahu begitu dapur selesai.",
      pickupWhereRefresh:
        "Muat ulang halaman ini untuk mengecek apakah sudah siap.",
      notificationCopy: {
        order_finished: {
          title: "Pesanan siap diambil",
          message:
            "Pesanan %{number} sudah selesai dan siap diambil di lantai 1, dekat dapur / lift.",
        },
      },
    },

    // Footer (HGS)
    footer: {
      copyright: "© 2025 HEBE - Jaringan Hotel, Hostel & Perhotelan",
      tagline: "Pengalaman Perjalanan Urban Berkelanjutan",
    },

    notifications: {
      on: "Aktif",
      off: "Mati",
      blocked: "Diblokir",
      alreadyEnabled:
        "Notifikasi aktif. Anda akan menerima update saat pesanan siap.",
      deniedMessage:
        "Notifikasi diblokir oleh browser. Untuk menerima update pesanan, silakan aktifkan notifikasi di pengaturan browser Anda.",
      promptModal: {
        title: "Dapatkan Update Pesanan Anda",
        description:
          "Aktifkan notifikasi agar kami bisa memberi tahu saat makanan Anda siap diambil. Jangan sampai ketinggalan!",
        enable: "Aktifkan Notifikasi",
        enabling: "Mengaktifkan...",
        later: "Nanti Saja",
      },
      deniedModal: {
        title: "Notifikasi Diblokir",
        description:
          "Sepertinya notifikasi diblokir di browser Anda. Ikuti langkah berikut untuk mengaktifkannya:",
        gotIt: "Mengerti",
        steps: {
          ios: [
            "Buka aplikasi Pengaturan di iPhone Anda",
            "Gulir ke bawah dan ketuk Safari (atau browser Anda)",
            "Ketuk Notifikasi dan aktifkan Izinkan Notifikasi",
            "Kembali ke halaman ini dan muat ulang",
          ],
          android: [
            "Ketuk ikon gembok (atau menu ⋮) di bilah alamat browser",
            "Ketuk Setelan situs atau Izin",
            "Temukan Notifikasi dan ubah menjadi Izinkan",
            "Kembali ke halaman ini dan muat ulang",
          ],
          androidFirefox: [
            "Ketuk ikon gembok di bilah alamat",
            "Ketuk Edit Pengaturan Situs",
            "Atur Notifikasi menjadi Diizinkan",
            "Kembali ke halaman ini dan muat ulang",
          ],
          desktopChrome: [
            "Klik ikon gembok (atau ikon tune) di bilah alamat",
            "Temukan Notifikasi di daftar izin",
            "Ubah dari Blokir menjadi Izinkan",
            "Muat ulang halaman ini",
          ],
          desktopFirefox: [
            "Klik ikon gembok di bilah alamat",
            "Klik bagian Izin",
            "Temukan Notifikasi dan hapus pemblokiran",
            "Muat ulang halaman ini",
          ],
          desktopSafari: [
            "Buka menu Safari → Pengaturan → Situs Web",
            "Klik Notifikasi di sidebar",
            "Temukan situs ini dan ubah menjadi Izinkan",
            "Muat ulang halaman ini",
          ],
        },
      },
    },

    // Landing Page
    landing: {
      stories: {
        eyebrow: "Instagram & TikTok",
        heading: "Dari properti",
        body: "Seperti apa tempat ini minggu ini, diunggah oleh orang-orang yang bekerja di sini.",
        follow: "Ikuti kami",
        watchOn: "Tonton di TikTok",
        viewOn: "Lihat di Instagram",
        empty: "Belum ada — ikuti kami dulu, ya.",
      },
      nav: {
        book: "Pesan",
        stories: "Cerita",
        guestPortal: "Sedang menginap",
        sections: "Jelajahi",
        home: "Beranda",
        about: "Tentang",
        rooms: "Kamar",
        facilities: "Fasilitas",
        gallery: "Galeri",
        insights: "Insights",
        contact: "Kontak",
        bookNow: "Pesan Sekarang",
      },
      hero: {
        eyebrow: "Hostel ramah lingkungan · Bogor, Jawa Barat",
        name: "Hebe Hostel Bogor",
        bookStay: "Pesan kamar",
        discover: "Selengkapnya",
        scrollHint: "Gulir ke konten",
        slideLabel: "Tampilkan gambar {n} dari {total}",
        galleryLabel: "Gambar utama",
        videoLabel: "Tur singkat tanpa suara di properti kami",
      },
      about: {
        subtitle: "Tentang Kami",
        title: "Selamat Datang di",
        contentHeading: "Pengalaman Perjalanan Urban Berkelanjutan",
        p1: "Misi kami adalah menawarkan pengalaman perjalanan urban yang berkelanjutan yang menghubungkan tamu yang peduli lingkungan dengan budaya lokal. Kami percaya dalam memaksimalkan kapasitas rooftop di jantung kota, menawarkan glamping mewah dengan pemandangan urban yang menakjubkan.",
        p2: "Dari sistem self-check-in pintar hingga fasilitas ramah lingkungan kami, setiap aspek Hebe Hotel dirancang dengan keberlanjutan dan kenyamanan modern dalam pikiran.",
        description:
          "Eco hostel modern di jantung kota di mana alam bertemu teknologi. Tamu menikmati self check-in pintar, kehidupan berkelanjutan, dan suasana urban yang nyaman untuk nomaden digital dan wisatawan sadar.",
        features: {
          eco: {
            title: "Ramah Lingkungan",
            desc: "Praktik berkelanjutan dan inisiatif hijau di seluruh properti kami",
          },
          location: {
            title: "Lokasi Strategis",
            desc: "Di jantung Bogor dengan akses mudah ke atraksi kota",
          },
          service: {
            title: "Layanan Berkualitas",
            desc: "50+ staf berpengalaman yang berdedikasi untuk kenyamanan Anda",
          },
          community: {
            title: "Komunitas",
            desc: "Sempurna untuk nomaden digital dan wisatawan yang sadar",
          },
        },
      },
      facilities: {
        subtitle: "Fasilitas",
        title: "Fasilitas Hotel",
        description:
          "Nikmati fasilitas berkelas dunia yang dirancang untuk kenyamanan dan kemudahan Anda",
        items: [
          {
            icon: "bed",
            title: "Kamar & Suite",
            description:
              "Berbagai jenis kamar, dari standar hingga suite mewah, dilengkapi dengan perlengkapan modern.",
          },
          {
            icon: "shield",
            title: "Keamanan 24 Jam",
            description:
              "Petugas keamanan di lokasi dan pengawasan terbaik dengan penyimpanan aman untuk barang berharga.",
          },
          {
            icon: "pool",
            title: "Kolam Renang",
            description:
              "Kolam dalam dan luar ruangan untuk bersantai atau berolahraga dengan pemandangan kota yang menakjubkan.",
          },
          {
            icon: "wifi",
            title: "WiFi Kecepatan Tinggi",
            description:
              "Akses internet kecepatan tinggi gratis di seluruh properti.",
          },
          {
            icon: "dining",
            title: "Restoran & Bar",
            description:
              "Restoran di lokasi dengan masakan lokal dan internasional, buka sepanjang hari.",
          },
        ],
      },
      rooms: {
        fromLabel: "Mulai",
        perNight: "per malam",
        roomsUnit: "kamar",
        wifi: "WiFi gratis",
        viewRoom: "Lihat kamar",
        subtitle: "Akomodasi",
        title: "Kamar Kami",
        description:
          "Pilih dari kamar kami yang dirancang dengan penuh perhatian, masing-masing dibuat untuk kenyamanan dan keberlanjutan",
        checkRates: "Cek Harga & Ketersediaan",
      },
      gallery: {
        photos: "foto",
        viewImage: "Lihat gambar",
        lightboxLabel: "Foto properti",
        close: "Tutup",
        prev: "Gambar sebelumnya",
        next: "Gambar berikutnya",
        subtitle: "Galeri",
        title: "Properti Kami",
        description: "Sekilas pandang ruang dan pengalaman kami",
      },
      booking: {
        occupancyNote:
          "Setiap kamar untuk dua tamu. Tamu ketiga atau keempat bisa menambah extra bed, dikenakan biaya per kamar per malam. Anak di bawah 5 tahun dan di bawah 100 cm menginap gratis tanpa perlu extra bed.",
        photos: "%{n} foto",
        viewPhotos: "Lihat foto",
        poolAccess: "Akses kolam renang",
        selfCheckIn: "Self check-in",
        checkInFrom: "Check-in mulai %{t}",
        checkOutBy: "Check-out maksimal %{t}",
        earlyCheckIn: "Early check-in",
        earlyCheckInNote:
          "Bisa diminta, tergantung ketersediaan pada hari itu, dengan biaya tambahan.",
        stayPolicy: "Waktu menginap",
        sleeps: "Maksimal %{n} tamu",
        roomSize: "%{n} m²",
        freeWifi: "WiFi gratis",
        perRoomPerNight: "per kamar, per malam",
        includedTitle: "Setiap kamar sudah termasuk",
        incl1: "Dua tamu, tanpa biaya tambahan",
        incl2: "Self check-in, kapan saja",
        incl3: "WiFi gratis di seluruh area",
        incl4: "Anak di bawah 5 tahun gratis",
        selectDates: "Pilih tanggal",
        clearDates: "Hapus",
        prevMonth: "Bulan sebelumnya",
        nextMonth: "Bulan berikutnya",
        addDates: "Pilih tanggal",
        maxStay: "Menginap maksimal %{n} malam",
        doneDates: "Selesai",
        roomsLabel: "Kamar",
        eyebrow: "Cek ketersediaan",
        heading: "Cari kamar Anda",
        checkIn: "Check in",
        checkOut: "Check out",
        nights: "malam",
        night: "malam",
        search: "Cek ketersediaan",
        searching: "Mengecek…",
        perNight: "per malam",
        totalFor: "Total untuk",
        available: "Tersedia",
        limited: "Tinggal sedikit",
        soldOut: "Penuh",
        guests: "Tamu",
        guestsHint: "Usia 5+ atau di atas 100cm",
        children: "Anak kecil",
        childrenHint: "Di bawah 5 th & 100cm · gratis",
        extraBed: "Extra bed",
        extraBedNote:
          "Pesan langsung dengan resepsionis kami. Tanpa komisi perantara, jadi kami sering bisa memberi harga lebih baik daripada situs pemesanan — dan Anda berbicara dengan orang yang akan menyerahkan kunci Anda.",
        add: "Tambah",
        remove: "Hapus",
        added: "Ditambahkan",
        yourStay: "Menginap Anda",
        emptyBasket: "Pilih kamar untuk mulai.",
        roomsLine: "kamar",
        estimatedTotal: "Perkiraan total",
        bookViaWhatsapp: "Pesan via WhatsApp",
        priceNote:
          "Harga yang ditampilkan adalah perkiraan tarif walk-in untuk tanggal yang dipilih. Tarif dapat berubah sesuai ketentuan properti, dan hotel dapat mengubahnya sewaktu-waktu sepenuhnya atas pertimbangan sendiri. Tarif final dikonfirmasi oleh resepsionis.",
        noRooms:
          "Tidak ada kamar untuk tanggal tersebut. Coba tanggal lain atau hubungi kami — mungkin kami masih bisa bantu.",
        pickDates:
          "Pilih tanggal untuk melihat tarif dan ketersediaan terkini.",
        alsoOn: "Tersedia juga di",
        subtitle: "Pesan Menginap",
        title: "Reservasi Kamar Anda",
        description:
          "Cek harga dan ketersediaan terkini melalui mitra pemesanan kami",
        stats: {
          roomsLabel: "Kamar",
          roomTypesLabel: "Tipe kamar",
          supportLabel: "Resepsionis",
          supportValue: "24/7",
        },
      },
      footer: {
        tagline: "Pengalaman Wisata Kota Ramah Lingkungan",
        description:
          "Eco hostel modern di jantung kota di mana alam bertemu teknologi.",
        quickLinks: "Tautan Cepat",
        services: "Layanan",
        contactUs: "Hubungi Kami",
        newsletter: "Newsletter",
        newsletterDesc: "Berlangganan untuk pembaruan dan penawaran eksklusif",
        subscribe: "Berlangganan",
        emailPlaceholder: "Masukkan email Anda",
        copyright: "Hebe Hostel Bogor. Semua hak dilindungi.",
        links: {
          home: "Beranda",
          about: "Tentang Kami",
          rooms: "Kamar",
          facilities: "Fasilitas",
          gallery: "Galeri",
          contact: "Kontak",
        },
        serviceLinks: {
          fnb: "Makanan & Minuman",
          roomService: "Layanan Kamar",
          activities: "Aktivitas",
          ecoTours: "Wisata Eko",
        },
        privacyPolicy: "Kebijakan Privasi",
        termsOfService: "Syarat & Ketentuan",
      },
      guestWidget: {
        askTitle: "Ada pertanyaan? Hubungi kami di WhatsApp",
        askDesc: "Membuka chat dengan resepsionis",
        title: "Layanan Tamu",
        subtitle: "Bagaimana kami bisa membantu?",
        guestServices: "Layanan Tamu",
        guestServicesDesc: "Akses semua layanan hotel",
        orderFood: "Pesan Makanan & Minuman",
        orderFoodDesc: "Jelajahi dan pesan dari menu kami",
      },
      privacyPolicy: {
        title: "Kebijakan Privasi",
        lastUpdated: "Terakhir diperbarui: Maret 2026",
        sections: [
          {
            heading: "1. Pendahuluan",
            body: 'Hebe Hotel, Hostel & Hospitality Network ("kami"), yang dioperasikan oleh HostelNMC dan berlokasi di Bogor, Jawa Barat, Indonesia, berkomitmen untuk melindungi data pribadi Anda. Kebijakan Privasi ini menjelaskan informasi apa yang kami kumpulkan, bagaimana kami menggunakannya, dan hak-hak Anda berdasarkan Undang-Undang Perlindungan Data Pribadi (UU PDP No. 27 Tahun 2022) serta standar internasional yang berlaku.',
          },
          {
            heading: "2. Informasi yang Kami Kumpulkan",
            body: "Kami mengumpulkan informasi yang Anda berikan saat melakukan reservasi atau menggunakan layanan kami, meliputi: nama lengkap, alamat email, nomor telepon, identitas resmi (KTP/paspor), detail pembayaran, dan preferensi menginap. Kami juga secara otomatis mengumpulkan informasi perangkat, alamat IP, jenis browser, dan data penggunaan saat Anda mengunjungi situs web kami.",
          },
          {
            heading: "3. Penggunaan Informasi Anda",
            body: "Informasi Anda digunakan untuk: memproses dan mengelola reservasi Anda, mengkomunikasikan konfirmasi dan pembaruan reservasi, menyediakan layanan tamu selama menginap, memproses pembayaran secara aman, memenuhi kewajiban hukum dan pajak Indonesia, meningkatkan layanan dan situs web kami, serta mengirimkan penawaran atau newsletter yang relevan (hanya dengan persetujuan Anda).",
          },
          {
            heading: "4. Berbagi Data",
            body: "Kami dapat berbagi data Anda dengan: agen perjalanan online (OTA) tempat reservasi Anda dilakukan, pemroses pembayaran (misalnya Xendit) untuk penanganan transaksi yang aman, otoritas pemerintah sesuai yang diwajibkan oleh hukum Indonesia, dan penyedia layanan yang beroperasi atas nama kami di bawah perjanjian kerahasiaan. Kami tidak menjual data pribadi Anda kepada pihak ketiga.",
          },
          {
            heading: "5. Keamanan Data",
            body: "Kami menerapkan langkah-langkah teknis dan organisasi standar industri untuk melindungi data pribadi Anda dari akses, perubahan, pengungkapan, atau penghancuran yang tidak sah. Data pembayaran ditangani sesuai dengan standar PCI-DSS.",
          },
          {
            heading: "6. Hak-Hak Anda",
            body: "Berdasarkan UU PDP No. 27 Tahun 2022, Anda berhak untuk mengakses, mengoreksi, atau meminta penghapusan data pribadi Anda. Anda juga dapat menarik persetujuan untuk komunikasi pemasaran kapan saja. Untuk menggunakan hak-hak ini, hubungi kami di admin@hostelnmc.com.",
          },
          {
            heading: "7. Cookie & Pelacakan",
            body: "Situs web kami menggunakan cookie dan teknologi serupa untuk memastikan fungsionalitas dan menganalisis penggunaan. Cookie esensial diperlukan agar situs berfungsi; cookie analitik hanya disetel dengan persetujuan Anda. Anda dapat mengelola preferensi cookie melalui pengaturan browser Anda.",
          },
          {
            heading: "8. Kontak",
            body: "Untuk pertanyaan apa pun tentang Kebijakan Privasi ini atau data pribadi Anda, silakan hubungi Petugas Perlindungan Data kami di admin@hostelnmc.com atau tulis kepada kami di Hebe Hotel, Bogor, Jawa Barat, Indonesia.",
          },
          {
            heading: "9. Komunikasi Deposit & Pengembalian Otomatis",
            body: "Jika Anda menggunakan portal deposit check-in mandiri kami, kami menggunakan alamat email dan nomor WhatsApp yang Anda konfirmasi di sana untuk berkomunikasi tentang deposit Anda dan memproses pengembaliannya secara otomatis setelah check-out: WhatsApp Anda menerima tautan pengembalian deposit dan email Anda menerima rincian payout serta konfirmasi. Kontak ini diproses oleh mitra pembayaran dan pesan kami (misalnya Xendit dan platform WhatsApp Business), yang dapat melibatkan transfer ke luar Indonesia dengan pengamanan yang sesuai. Kami menyimpannya selama masa inap Anda dan periode pemrosesan pengembalian. Anda bertanggung jawab atas keakuratan data yang Anda masukkan; kami tidak bertanggung jawab atas pengembalian yang gagal sampai kepada Anda akibat data kontak yang salah yang Anda berikan. Anda dapat mengoreksi data atau menarik persetujuan (dengan tetap menyelesaikan pengembalian yang sedang berjalan) berdasarkan UU PDP No. 27 Tahun 2022 dengan menghubungi admin@hostelnmc.com.",
          },
          {
            heading: "10. Pembaruan Kebijakan",
            body: "Kami dapat memperbarui Kebijakan Privasi ini dari waktu ke waktu. Tanggal di bagian atas halaman ini menunjukkan kapan terakhir kali direvisi. Kami mendorong Anda untuk meninjau halaman ini secara berkala.",
          },
        ],
      },
      termsOfService: {
        title: "Syarat & Ketentuan",
        lastUpdated: "Terakhir diperbarui: Maret 2026",
        sections: [
          {
            heading: "1. Persetujuan",
            body: 'Dengan melakukan reservasi atau menginap di Hebe Hotel, Hostel & Hospitality Network ("Hebe"), Anda setuju untuk terikat oleh Syarat & Ketentuan ini. Jika Anda tidak setuju, mohon jangan lanjutkan pemesanan Anda.',
          },
          {
            heading: "2. Pemesanan & Reservasi",
            body: "Waktu check-in adalah pukul 14:00 dan waktu check-out adalah pukul 12:00. Early check-in dan late check-out tergantung ketersediaan dan mungkin dikenakan biaya tambahan. Kebijakan pembatalan bervariasi berdasarkan paket tarif dan akan tercantum jelas saat pemesanan. No-show dapat dikenakan biaya penuh untuk seluruh reservasi.",
          },
          {
            heading: "3. Tanggung Jawab Tamu",
            body: "Tamu bertanggung jawab atas kerusakan yang ditimbulkan pada properti, furnitur, atau peralatan selama menginap. Hebe berhak untuk mengenakan biaya atas kerusakan yang dinilai setelah keberangkatan. Tamu harus memastikan perilaku mereka tidak mengganggu tamu atau staf lain.",
          },
          {
            heading: "4. Pembayaran",
            body: "Pembayaran diperlukan untuk mengkonfirmasi reservasi Anda. Kami menerima kartu kredit/debit utama dan metode pembayaran digital tertentu. Semua harga dikutip dalam Rupiah Indonesia (IDR) termasuk pajak yang berlaku. Hebe berhak untuk mengubah harga tanpa pemberitahuan sebelumnya, kecuali untuk pemesanan yang sudah dikonfirmasi.",
          },
          {
            heading: "5. Persyaratan Check-In",
            body: "Semua tamu harus menunjukkan kartu identitas foto resmi yang berlaku (KTP Indonesia atau paspor internasional) saat check-in. Tamu di bawah 18 tahun harus didampingi oleh orang tua atau wali sah. Hebe berhak menolak check-in jika identitas yang valid tidak dapat diberikan.",
          },
          {
            heading: "6. Peraturan Properti",
            body: "Merokok dilarang keras di dalam kamar tamu. Area merokok mungkin tersedia di beberapa area dalam dan luar ruangan properti kami — silakan tanyakan kepada staf yang bertugas jika Anda tidak yakin di mana merokok diperbolehkan. Jam tenang diberlakukan antara pukul 22:00 dan 07:00. Hewan peliharaan tidak diizinkan di dalam properti. Aktivitas ilegal apa pun akan mengakibatkan pemindahan segera dari properti dan dapat dilaporkan kepada pihak berwenang.",
          },
          {
            heading: "7. Batasan Tanggung Jawab",
            body: "Hebe tidak bertanggung jawab atas kehilangan, pencurian, atau kerusakan barang pribadi selama menginap kecuali disebabkan oleh kelalaian langsung kami. Kami menyarankan tamu untuk selalu menjaga dan mengamankan barang berharga dalam pengawasan pribadi. Tanggung jawab kami, jika ada, terbatas pada jumlah yang dibayarkan untuk akomodasi.",
          },
          {
            heading: "8. Hukum yang Berlaku",
            body: "Syarat & Ketentuan ini diatur oleh hukum Republik Indonesia. Setiap perselisihan yang timbul dari ketentuan ini tunduk pada yurisdiksi eksklusif pengadilan di Bogor, Jawa Barat, Indonesia.",
          },
          {
            heading: "9. Data Kontak Tamu & Pengembalian Deposit Otomatis",
            body: "Saat Anda membayar deposit secara online dan mengonfirmasi email serta nomor WhatsApp Anda, Anda menjamin bahwa data tersebut benar dan terkini, serta menyetujui Hebe menggunakannya untuk berkomunikasi tentang masa inap Anda dan mengirimkan pengembalian deposit otomatis Anda (tautan pengembalian dikirim via WhatsApp dan rincian payout via email). Anda sepenuhnya bertanggung jawab atas keakuratan data kontak yang Anda berikan; Hebe tidak bertanggung jawab atas pengembalian deposit yang tertunda atau gagal akibat informasi kontak yang salah, tidak aktif, atau salah ketik yang Anda masukkan. Biaya layanan online tidak dapat dikembalikan.",
          },
          {
            heading: "10. Kontak",
            body: "Jika Anda memiliki pertanyaan mengenai Syarat & Ketentuan ini, silakan hubungi kami di admin@hostelnmc.com atau kunjungi kami di Hebe Hotel, Bogor, Jawa Barat, Indonesia.",
          },
        ],
      },
      insights: {
        brand: "Insights",
        tagline: "Cerita, panduan, dan tips lokal dari Bogor",
        index: {
          title: "HEBE Insights",
          subtitle:
            "Cerita, panduan, dan tips lokal untuk menginap yang lebih bermakna dan hijau di Bogor.",
          featured: "Pilihan",
          viewAll: "Lihat semua",
          exploreCategory: "Jelajahi",
          latestIn: "Terbaru di",
        },
        card: {
          minRead: "menit baca",
          by: "Oleh",
        },
        detail: {
          backToInsights: "Kembali ke Insights",
          by: "Oleh",
          minRead: "menit baca",
          inCategory: "di",
          share: "Bagikan cerita ini",
          related: "Cerita terkait",
          copyLink: "Salin tautan",
          copied: "Tersalin!",
        },
        category: {
          articles: "artikel",
          allArticles: "Semua artikel",
          empty: "Belum ada artikel di sini. Nantikan segera.",
        },
        pagination: {
          prev: "Sebelumnya",
          next: "Berikutnya",
          page: "Halaman",
          of: "dari",
        },
        breadcrumb: {
          home: "Beranda",
          insights: "Insights",
        },
        share: {
          whatsapp: "WhatsApp",
          facebook: "Facebook",
          x: "X",
          telegram: "Telegram",
          line: "LINE",
          linkedin: "LinkedIn",
          pinterest: "Pinterest",
          email: "Email",
          copy: "Salin tautan",
        },
      },
    },
  },
};

export const languages = [
  { code: "en", label: "EN", name: "English", flag: "🇬🇧" },
  { code: "id", label: "ID", name: "Indonesia", flag: "🇮🇩" },
];
